import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faStar, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES, STORAGE_KEYS } from '@/constants';
import { useCities } from '@/api/useLocations';
import {
  Card,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
  Paginator,
} from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { useModalStore, useAuthStore } from '@/stores';
import { useUsers } from '@/api/useUsers';
import { DeleteProviderModal, ToggleProviderStateModal } from '@/components/modals/ProviderModals';
import type { ProviderContact } from '@/types/provider';
import styles from './Providers.module.css';

const PAGE_SIZE = 8;

interface StoredFilters {
  search: string;
  stateFilter: string;
  countryFilter: string;
  cityFilter: string;
  serviceFilter: string;
  creatorFilter: string;
}

const EMPTY_FILTERS: StoredFilters = {
  search: '',
  stateFilter: '',
  countryFilter: '',
  cityFilter: '',
  serviceFilter: '',
  creatorFilter: '',
};

const loadStoredFilters = (fallbackCountry: string): StoredFilters => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PROVIDER_FILTERS);
    if (raw) return { ...EMPTY_FILTERS, ...(JSON.parse(raw) as Partial<StoredFilters>) };
  } catch {
    /* ignored — fall through to default */
  }
  return { ...EMPTY_FILTERS, countryFilter: fallbackCountry };
};

interface ProviderService {
  id: string;
  service_details: {
    id: string;
    name: string;
    slug: string;
  };
}

interface Provider {
  id: string;
  nombre_comercial: string;
  ruc: string;
  email: string;
  telefono: string;
  pais: string;
  provincia: string;
  direccion: string;
  ciudad: string;
  distrito: string;
  state: string;
  calificacion: number | null;
  contacts?: ProviderContact[];
  services?: ProviderService[];
  temp_creator?: { name: string; email: string; country: string } | null;
  created_by_email?: string | null;
}

interface ProvidersResponse {
  count: number;
  results: Provider[];
}

export function Providers() {
  const navigate = useNavigate();
  const { setHeaderTitle, setHeaderSlot } = useOutletContext<{
    setHeaderTitle: (title: string) => void;
    setHeaderSlot: (slot: React.ReactNode) => void;
  }>();
  const { openModal } = useModalStore();
  const { isStaffOrAbove, isProviderUser, user } = useAuthStore();
  const [filtersInit] = useState<StoredFilters>(() => loadStoredFilters(user?.country || ''));
  const [search, setSearch] = useState(filtersInit.search);
  const [currentPage, setCurrentPage] = useState(1);
  const [stateFilter, setStateFilter] = useState(filtersInit.stateFilter);
  const [countryFilter, setCountryFilter] = useState(filtersInit.countryFilter);
  const [cityFilter, setCityFilter] = useState(filtersInit.cityFilter);
  const { data: citiesForFilter } = useCities(countryFilter || 'Peru');
  const [serviceFilter, setServiceFilter] = useState(filtersInit.serviceFilter);
  const [creatorFilter, setCreatorFilter] = useState(filtersInit.creatorFilter);

  // Reset page to 1 on filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, stateFilter, countryFilter, cityFilter, serviceFilter, creatorFilter]);

  // Persist filters to localStorage so they survive reloads / route changes.
  // Cleared on logout via useAuthStore's storage.clear().
  useEffect(() => {
    const data: StoredFilters = {
      search,
      stateFilter,
      countryFilter,
      cityFilter,
      serviceFilter,
      creatorFilter,
    };
    localStorage.setItem(STORAGE_KEYS.PROVIDER_FILTERS, JSON.stringify(data));
  }, [search, stateFilter, countryFilter, cityFilter, serviceFilter, creatorFilter]);

  const hasActiveFilters = Boolean(
    search || stateFilter || countryFilter || cityFilter || serviceFilter || creatorFilter,
  );

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setStateFilter('');
    setCountryFilter('');
    setCityFilter('');
    setServiceFilter('');
    setCreatorFilter('');
  }, []);

  // Fetch services for filter
  const { data: servicesData } = useQuery({
    queryKey: ['services-light'],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.servicesLight);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch providers
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      'providers',
      currentPage,
      search,
      stateFilter,
      countryFilter,
      cityFilter,
      serviceFilter,
      creatorFilter,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String((currentPage - 1) * PAGE_SIZE));
      if (search) params.append('search', search);
      if (stateFilter) params.append('state', stateFilter);
      if (countryFilter) params.append('country', countryFilter);
      if (cityFilter) params.append('ciudad', cityFilter);
      if (serviceFilter) params.append('services__service', serviceFilter);
      if (creatorFilter) params.append('created_by', creatorFilter);

      const response = await api.get<ProvidersResponse>(`${API_ROUTES.providers}?${params}`);
      return response.data;
    },
  });

  // Fetch stats
  useQuery({
    queryKey: ['provider-stats'],
    queryFn: async () => {
      const response = await api.get(`${API_ROUTES.providers}stats/`);
      return {
        totalProviders: response.data.total_providers,
        activeProviders: response.data.active_providers,
        totalServices: response.data.total_services,
        totalOfferings: response.data.total_offerings,
      };
    },
  });

  const handleAddProvider = useCallback(() => {
    navigate(ROUTES.dashboardProvidersNew);
  }, [navigate]);

  const handleViewProvider = (provider: Provider) => {
    navigate(ROUTES.dashboardProviderDetails.replace(':id', String(provider.id)));
  };

  const handleEditProvider = (provider: Provider) => {
    navigate(`/dashboard/providers/${provider.id}/edit`);
  };

  const handleDeleteProvider = (provider: Provider) => {
    openModal(<DeleteProviderModal provider={provider} onSuccess={() => refetch()} />);
  };

  const handleToggleState = (provider: Provider) => {
    openModal(<ToggleProviderStateModal provider={provider} onSuccess={() => refetch()} />);
  };

  const getPrimaryPhone = (provider: Provider): string | undefined => {
    const primaryContact = provider.contacts?.find((c: ProviderContact) => c.es_principal);
    return primaryContact?.telefono || provider.contacts?.[0]?.telefono || provider.telefono || undefined;
  };

  const handleWhatsApp = (provider: Provider) => {
    const phone = getPrimaryPhone(provider);
    if (!phone) return;
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 7) return;
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const isValidPhone = (phone: string | undefined) => {
    if (!phone) return false;
    const clean = phone.replace(/\D/g, '');
    return clean.length >= 7;
  };

  const stateOptions = [
    { value: '', label: 'All statuses' },
    { value: 'activo', label: 'Active' },
    { value: 'inactivo', label: 'Inactive' },
  ];

  const countryOptions = [
    { value: '', label: 'All countries' },
    { value: 'Peru', label: 'Peru' },
    { value: 'Mexico', label: 'Mexico' },
    { value: 'Spain', label: 'Spain' },
  ];

  const cityOptions = [{ value: '', label: 'All cities' }, ...(citiesForFilter ?? [])];

  const serviceOptions = [
    { value: '', label: 'Any service' },
    ...(servicesData?.map((s: { id: string; name: string }) => ({
      value: String(s.id),
      label: s.name,
    })) || []),
  ];

  const { data: users } = useUsers();
  const creatorOptions = [
    { value: '', label: 'All creators' },
    ...(users ?? []).map((u) => ({
      value: String(u.id),
      label: `${u.first_name} ${u.last_name}`.trim() || u.email,
    })),
  ];

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  useEffect(() => {
    setHeaderTitle(isProviderUser() ? 'My Providers' : 'Providers');
    setHeaderSlot(
      isStaffOrAbove() ? (
        <Button variant="primary" onClick={handleAddProvider} size="sm">
          <FontAwesomeIcon icon={faPlus} />
          <span className={styles.hideOnMobile}>Add Provider</span>
        </Button>
      ) : null,
    );
    return () => {
      setHeaderTitle('');
      setHeaderSlot(null);
    };
  }, [setHeaderTitle, setHeaderSlot, isProviderUser, isStaffOrAbove, handleAddProvider]);

  return (
    <div className={styles.container}>
      {/* Providers Table */}
      <Card className={styles.tableCard}>
        {/* Filters */}
        <div className={styles.filters}>
          <Input
            name="search"
            label="Search"
            placeholder="Search by name, email..."
            value={search}
            setValue={setSearch}
            variant="bordered"
            isClearable
          />
          <Select
            label="Status"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            options={stateOptions}
            size="md"
          />
          <Select
            label="Country"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            options={countryOptions}
            size="md"
          />
          <Select
            label="City"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            options={cityOptions}
            size="md"
            searchable
          />
          <Select
            label="Service"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            options={serviceOptions}
            size="md"
            searchable
          />
          {isStaffOrAbove() && (
            <Select
              label="Creator"
              value={creatorFilter}
              onChange={(e) => setCreatorFilter(e.target.value)}
              options={creatorOptions}
              size="md"
              searchable
            />
          )}
          {hasActiveFilters && (
            <div className={styles.clearFiltersCell}>
              <Button variant="secondary" size="sm" onClick={handleClearFilters}>
                <FontAwesomeIcon icon={faXmark} />
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <Table aria-label="Providers list" radius={8}>
          <TableHeader>
            <TableColumn>Trade Name</TableColumn>
            <TableColumn>Services</TableColumn>
            <TableColumn>Email</TableColumn>
            <TableColumn>Phone</TableColumn>
            <TableColumn>City</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Rating</TableColumn>
            {isStaffOrAbove() && <TableColumn>Creator</TableColumn>}
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody isLoading={isLoading || isFetching}>
            {data?.results.map((provider) => (
              <TableRow
                key={provider.id}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest(`.${styles.actionButtons}`)) return;
                  handleViewProvider(provider);
                }}
                style={{ cursor: 'pointer' }}
              >
                <TableCell>{provider.nombre_comercial}</TableCell>
                <TableCell>
                  <div className={styles.servicesContainer}>
                    {provider.services && provider.services.length > 0
                      ? provider.services.map((ps) => (
                          <span key={ps.id} className={styles.serviceChip}>
                            {ps.service_details.name}
                          </span>
                        ))
                      : '-'}
                  </div>
                </TableCell>
                <TableCell>{provider.contacts?.find((c: ProviderContact) => c.es_principal)?.email || provider.contacts?.[0]?.email || provider.email || '-'}</TableCell>
                <TableCell>{provider.contacts?.find((c: ProviderContact) => c.es_principal)?.telefono || provider.contacts?.[0]?.telefono || provider.telefono || '-'}</TableCell>
                <TableCell>{[provider.ciudad, provider.pais].filter(Boolean).join(', ')}</TableCell>
                <TableCell>
                  {isStaffOrAbove() ? (
                    <span
                      className={`${styles.statusBadge} ${styles.statusBadgeClickable} ${provider.state === 'activo' ? styles.statusActive : styles.statusInactive}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleState(provider);
                      }}
                      title={
                        provider.state === 'activo' ? 'Click to deactivate' : 'Click to activate'
                      }
                    >
                      {provider.state === 'activo' ? 'Active' : 'Inactive'}
                    </span>
                  ) : (
                    <span
                      className={`${styles.statusBadge} ${provider.state === 'activo' ? styles.statusActive : styles.statusInactive}`}
                    >
                      {provider.state === 'activo' ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {provider.calificacion ? (
                    <span className={styles.rating}>
                      <FontAwesomeIcon icon={faStar} className={styles.ratingIcon} />
                      {provider.calificacion.toFixed(1)}
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                {isStaffOrAbove() && (
                  <TableCell>
                    <span
                      className={styles.creatorCell}
                      title={provider.created_by_email || provider.temp_creator?.email || ''}
                    >
                      {provider.created_by_email
                        ? provider.created_by_email.split('@')[0]
                        : provider.temp_creator?.name || '—'}
                    </span>
                  </TableCell>
                )}
                <TableCell>
                  <div className={styles.actionButtons}>
                    <Button
                      variant="secondary"
                      size="sm"
                      className={styles.buttonWhatsapp}
                      disabled={!isValidPhone(getPrimaryPhone(provider))}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsApp(provider);
                      }}
                      title={
                        isValidPhone(getPrimaryPhone(provider))
                          ? 'Contact via WhatsApp'
                          : 'Invalid phone number'
                      }
                    >
                      <FontAwesomeIcon icon={faWhatsapp} />
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProvider(provider);
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    {isStaffOrAbove() && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProvider(provider);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {data?.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={isStaffOrAbove() ? 9 : 8}>
                  <div className={styles.emptyState}>
                    {isProviderUser()
                      ? 'No provider assigned to your account. Contact an administrator.'
                      : 'No providers found'}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Paginator page={currentPage} numPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
